import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Gavel,
  HelpCircle,
  Lightbulb,
  Shield,
  Users,
  User,
  BriefcaseBusiness,
  FileSearch2,
  FileCheck,
  ArrowRightLeft,
  ListTodo,
  NotepadText,
  Coins,
  Square,
  Triangle,
  Shapes,
  LucideIcon,
} from "lucide-react";

import { cn } from "../../lib/utils";
import NavButton from "./NavButton";
import FeatureGate from "../featureFlags/components/FeatureGate";
import { isBoardroomPresentationEnabled, isFraudElementsEnabled } from "../configs/logic/Slice";
import { isAccountDetailsNavEnabled, isCreditAreaOfFraud, isInspectionsAndAuditsNavEnabled } from "../caseinfo/logic/Slice";
import SidebarUserMenu from "./SidebarUserMenu";
import { RootState, useAppSelector } from "../../../src/app/store";
import { selectCaseHeader } from "../caseinfo/logic/Slice";
import SidebarCollapseButton from "./SidebarCollapseButton";
import SidebarHeader from "./SidebarHeader";
import { Permission, useAbility } from "../auth/logic/rbac";
import {
  CaseRouteId,
  getCaseRoutePath,
  resolveCaseRouteIdFromPath,
} from "../caselayout/config/caseRoutes";

interface WorkbenchSubNavButtonProps {
  isActive: boolean;
  label: string;
  onClick: () => void;
}

const WorkbenchSubNavButton: React.FC<WorkbenchSubNavButtonProps> = ({
  isActive,
  label,
  onClick,
}) => (
  <button
    type="button"
    className={cn(
      "flex h-7 w-full items-center rounded-[8px] px-2 text-left text-[13px] leading-5 transition-colors duration-100",
      isActive
        ? "bg-[#F5F5F5] font-medium text-[#171717]"
        : "text-[#6B7280] hover:bg-[#F5F5F5]",
    )}
    onClick={onClick}
  >
    <span className="truncate">{label}</span>
  </button>
);

interface CaseNavChildrenProps {
  label: string;
  routeId: CaseRouteId;
  featureGateSelector?: (state: RootState) => boolean
}

interface CaseNavButtonWithChildrenProps {
  label: string;
  icon: LucideIcon;
  defaultRouteId: CaseRouteId;
  activeRouteId: CaseRouteId | null;
  isCollapsed: boolean;
  headerPermission?: Permission;
  subNavs: CaseNavChildrenProps[];
}

const CaseNavButtonWithChildren: React.FC<CaseNavButtonWithChildrenProps> = ({
  label,
  icon,
  defaultRouteId,
  activeRouteId,
  isCollapsed,
  headerPermission,
  subNavs
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const { can } = useAbility();
  const navigate = useNavigate();

  const childRouteIds = subNavs.map((nav) => nav.routeId);
  const isHeaderActive = activeRouteId? childRouteIds.includes(activeRouteId) : false;

  const canAccessHeader = headerPermission? can(headerPermission): true;

  const navigateToRouteId = (routeId: CaseRouteId) => {
    if (!caseId) return;
    navigate(getCaseRoutePath(caseId, routeId));
  }

  return (
    <>
      <NavButton
        label={label}
        icon={icon}
        isActive={isHeaderActive}
        onClick={() => {navigateToRouteId(defaultRouteId)}}
        isCollapsed={isCollapsed}
        disabled={!canAccessHeader}
      />
      {!isCollapsed && isHeaderActive && canAccessHeader && (
        <div className="relative flex flex-col gap-1 py-0.5 pl-6">
          <div className="absolute inset-y-0 left-4 w-px bg-[#E5E5E5]" />
            {subNavs.map((subNav) => (
              subNav.featureGateSelector ? 
              <FeatureGate selector={subNav.featureGateSelector}>
                <WorkbenchSubNavButton
                  key={subNav.routeId}
                  label={subNav.label}
                  isActive={activeRouteId === subNav.routeId}
                  onClick={() => navigateToRouteId(subNav.routeId)}
                />
              </FeatureGate>:
                <WorkbenchSubNavButton
                  key={subNav.routeId}
                  label={subNav.label}
                  isActive={activeRouteId === subNav.routeId}
                  onClick={() => navigateToRouteId(subNav.routeId)}
                />
            ))}
          </div>
        )
      }
    </>
    
  )
}

const CaseNavigationSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: caseId } = useParams<{ id: string }>();
  const { can } = useAbility();

  const canAccessObservationsByAuthority = can(
    Permission.OBSERVATIONS_BY_AUTHORITY,
  );

  const canAccessInvestigationReport = can(
    Permission.ACCESS_INVESTIGATION_REPORT,
  );

  const canAccessCaseReports = can(
    Permission.ACCESS_CASE_REPORTS,
  );

  const canAccessRecommendations = can(
    Permission.ACCESS_RECOMMENDATIONS,
  );

  const caseData = useAppSelector(selectCaseHeader);
  const caseUIN = caseData?.uin || caseId || "";

  const activeCaseNav = resolveCaseRouteIdFromPath(location.pathname, caseId);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const isAccountDetailsEnabled = useAppSelector(isAccountDetailsNavEnabled);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  useEffect(() => {
    if (activeCaseNav === CaseRouteId.FRAUD_ELEMENTS) {
      setIsCollapsed(true);
    }
  }, [activeCaseNav]);

  return (
    <div className="relative">
      <aside
        className={cn(
          "flex flex-col h-full bg-sidebar border text-sidebar-foreground",
          "transition-all duration-100 ease-in-out",
          isCollapsed ? "w-14" : "w-64",
        )}
        data-collapsed={isCollapsed}
      >
        <div className="flex flex-col h-full bg-white">
          <SidebarHeader isCollapsed={isCollapsed} />

          <div className="flex flex-col flex-1 overflow-hidden">
            <nav className="flex flex-col flex-1 overflow-y-auto p-2 gap-3">
              {/* --- SECTION: CASE HEADER / UIN --- */}
              <div className="flex flex-col gap-[12.5px]">
                <div className={cn("flex flex-col", isCollapsed ? "items-center" : "")}>
                  {isCollapsed ? (
                    <button
                      className="h-8 w-full flex items-center justify-center rounded-[8px] hover:bg-[#F5F5F5]"
                      onClick={() => navigate("/cases")}
                      title="Back to Cases"
                    >
                      <ArrowLeft className="h-4 w-4 text-[#737373]" />
                    </button>
                  ) : (
                    <div className="flex items-center text-gray-900 font-semibold text-xs">
                      <button
                        className="h-7 w-7 flex items-center justify-center rounded-[8px] hover:bg-[#F5F5F5] transition-colors flex-shrink-0"
                        onClick={() => navigate("/cases")}
                        title="Back to Cases"
                      >
                        <ArrowLeft className="h-4 w-4 mr-0 text-[#737373]" />
                      </button>

                      <span
                        className="truncate max-w-[200px] text-[13px] font-semibold text-[#737373]"
                        title={`Case ${caseUIN}`}
                      >
                        Case {caseUIN}
                      </span>
                    </div>
                  )}
                </div>

                <div className="px-2">
                  <div className="h-[1px] w-full bg-[#E5E5E5]" />
                </div>
              </div>

              {/* --- SECTION: MAIN NAVIGATION GROUP --- */}
              <div className="flex flex-col gap-4">
                {/* GROUP: OVERVIEW */}
                <div className="flex flex-col gap-0.5">
                  <NavButton
                    label="Case Overview"
                    icon={BriefcaseBusiness}
                    isActive={activeCaseNav === CaseRouteId.CASE_OVERVIEW}
                    onClick={() => navigate(`/cases/${caseId}`)}
                    isCollapsed={isCollapsed}
                  />
                  <NavButton
                    label="Recommendations"
                    icon={Lightbulb}
                    isActive={activeCaseNav === CaseRouteId.RECOMMENDATIONS}
                    onClick={() => navigate(`/cases/${caseId}/recommendations`)}
                    isCollapsed={isCollapsed}
                    disabled={!canAccessRecommendations}
                  />
                </div>

                {/* GROUP: INVESTIGATION */}
                <div className="flex flex-col gap-3">
                  <div className={cn("flex h-[26px] items-center", isCollapsed ? "justify-center" : "px-2 gap-1.5")}>
                    {isCollapsed ? (
                      <Square size={6} fill="#6B7280" className="text-[#6B7280]" />
                    ) : (
                      <>
                        <span className="text-xs font-medium text-[#6B7280]">Investigation</span>
                        <Triangle size={7} fill="#6B7280" stroke="none" className="rotate-180" />
                      </>
                    )}
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <CaseNavButtonWithChildren
                      label="Investigation Workbench"
                      icon={FileSearch2}
                      defaultRouteId={isAccountDetailsEnabled ? CaseRouteId.ACCOUNT_DETAILS : CaseRouteId.ANALYSIS}
                      activeRouteId={activeCaseNav}
                      isCollapsed={isCollapsed}
                      headerPermission={Permission.ACCESS_INVESTIGATION}
                      subNavs={[
                        {
                          label: "Account Details",
                          routeId: CaseRouteId.ACCOUNT_DETAILS,
                          featureGateSelector: isAccountDetailsNavEnabled
                        },
                        {
                          label: "Fact Finding Analysis",
                          routeId: CaseRouteId.ANALYSIS
                        },
                        {
                          label: "Inspections & Audits",
                          routeId: CaseRouteId.INSPECTIONS_AUDITS,
                          featureGateSelector: isInspectionsAndAuditsNavEnabled
                        },
                        {
                          label: "Observations & Actions",
                          routeId: CaseRouteId.OBSERVATIONS_RECOMMENDATIONS
                        },
                        {
                          label: "Documents",
                          routeId: CaseRouteId.INVESTIGATION_DOCUMENTS
                        }
                      ]}  
                    />
                    <NavButton
                      label="Investigation Report"
                      icon={FileCheck}
                      isActive={activeCaseNav === CaseRouteId.IR}
                      onClick={() => {
                        if (!caseId) return;
                        navigate(getCaseRoutePath(caseId, CaseRouteId.IR));
                      }}
                      isCollapsed={isCollapsed}
                      disabled={!canAccessInvestigationReport}
                    />
                    <NavButton
                      label="Observations by Authority"
                      icon={User}
                      isActive={activeCaseNav === CaseRouteId.OBSERVATIONS_BY_AUTHORITY}
                      onClick={() => {
                        if (!caseId) return;
                        navigate(
                          getCaseRoutePath(
                            caseId,
                            CaseRouteId.OBSERVATIONS_BY_AUTHORITY,
                          ),
                        );
                      }}
                      isCollapsed={isCollapsed}
                      disabled={!canAccessObservationsByAuthority}
                    />
                  </div>
                </div>

                {/* GROUP: CLASSIFICATION */}
                <div className="flex flex-col gap-3">
                  <div className={cn("flex h-[26px] items-center", isCollapsed ? "justify-center" : "px-2 gap-1.5")}>
                    {isCollapsed ? (
                      <Square size={6} fill="#6B7280" className="text-[#6B7280]" />
                    ) : (
                      <>
                        <span className="text-xs font-medium text-[#6B7280]">Classification</span>
                        <Triangle size={7} fill="#6B7280" stroke="none" className="rotate-180" />
                      </>
                    )}
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <NavButton
                      label="FIC for SCN or No Fraud"
                      icon={Users}
                      isActive={activeCaseNav === CaseRouteId.REPORT_PRE_SCN_FIC}
                      onClick={() => {
                        if (!caseId) return;
                        navigate(getCaseRoutePath(caseId, CaseRouteId.REPORT_PRE_SCN_FIC));
                      }}
                      isCollapsed={isCollapsed}
                      disabled={!canAccessCaseReports}
                    />
                    <FeatureGate selector={isFraudElementsEnabled}>
                      <NavButton
                        label="Elements of Fraud"
                        icon={Shapes}
                        isActive={activeCaseNav === CaseRouteId.FRAUD_ELEMENTS}
                        onClick={() => {
                          if (!caseId) return;
                          navigate(getCaseRoutePath(caseId, CaseRouteId.FRAUD_ELEMENTS));
                        }}
                        isCollapsed={isCollapsed}
                        disabled={!canAccessCaseReports}
                      />
                    </FeatureGate>
                    <NavButton
                      label="Show Cause Notice"
                      icon={ArrowRightLeft}
                      isActive={activeCaseNav === CaseRouteId.REPORT_SCN}
                      onClick={() => {
                        if (!caseId) return;
                        navigate(getCaseRoutePath(caseId, CaseRouteId.REPORT_SCN));
                      }}
                      isCollapsed={isCollapsed}
                      disabled={!canAccessCaseReports}
                    />
                    <NavButton
                      label="FIC for Final Decision"
                      icon={Users}
                      isActive={activeCaseNav === CaseRouteId.REPORT_FIC}
                      onClick={() => {
                        if (!caseId) return;
                        navigate(getCaseRoutePath(caseId, CaseRouteId.REPORT_FIC));
                      }}
                      isCollapsed={isCollapsed}
                      disabled={!canAccessCaseReports}
                    />
                    <NavButton
                      label="Reasoned Order"
                      icon={Shield}
                      isActive={activeCaseNav === CaseRouteId.REPORT_RO}
                      onClick={() => {
                        if (!caseId) return;
                        navigate(getCaseRoutePath(caseId, CaseRouteId.REPORT_RO));
                      }}
                      isCollapsed={isCollapsed}
                      disabled={!canAccessCaseReports}
                    />
                  </div>
                </div>

                {/* GROUP: REPORTING */}
                <div className="flex flex-col gap-3">
                  <div className={cn("flex h-[26px] items-center", isCollapsed ? "justify-center" : "px-2 gap-1.5")}>
                    {isCollapsed ? (
                      <Square size={6} fill="#6B7280" className="text-[#6B7280]" />
                    ) : (
                      <>
                        <span className="text-xs font-medium text-[#6B7280]">Reporting</span>
                        <Triangle size={7} fill="#6B7280" stroke="none" className="rotate-180" />
                      </>
                    )}
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <NavButton
                      label="FMR 1"
                      icon={ListTodo}
                      isActive={activeCaseNav === CaseRouteId.REPORT_FMR}
                      onClick={() => {
                        if (!caseId) return;
                        navigate(getCaseRoutePath(caseId, CaseRouteId.REPORT_FMR));
                      }}
                      isCollapsed={isCollapsed}
                      disabled={!canAccessCaseReports}
                    />
                    <NavButton
                      label="LEA Report"
                      icon={Gavel}
                      isActive={activeCaseNav === CaseRouteId.REPORT_LEA}
                      onClick={() => {
                        if (!caseId) return;
                        navigate(getCaseRoutePath(caseId, CaseRouteId.REPORT_LEA));
                      }}
                      isCollapsed={isCollapsed}
                      disabled={!canAccessCaseReports}
                    />
                    <FeatureGate selector={isBoardroomPresentationEnabled}>
                      <NavButton
                        label="Board Report"
                        icon={NotepadText}
                        isActive={activeCaseNav === CaseRouteId.REPORT_BOARD}
                        onClick={() => navigate(`/cases/${caseId}/reports/board-ppt`)}
                        isCollapsed={isCollapsed}
                        disabled={!canAccessCaseReports}
                      />
                    </FeatureGate>
                    <FeatureGate selector={isCreditAreaOfFraud}>
                      <NavButton
                        label="Recovery"
                        icon={Coins}
                        isActive={activeCaseNav === CaseRouteId.REPORT_RECOVERY}
                        onClick={() => {
                          if (!caseId) return;
                          navigate(getCaseRoutePath(caseId, CaseRouteId.REPORT_RECOVERY));
                        }}
                        isCollapsed={isCollapsed}
                        disabled={!canAccessCaseReports}
                      />
                    </FeatureGate>
                  </div>
                </div>
              </div>
            </nav>
          </div>

          {/* --- SECTION: FOOTER (SUPPORT & USER MENU) --- */}
          <div className="flex flex-col px-2 pb-2 pt-0 gap-2 mt-auto bg-[#FAFAFA]">
            <div className={cn(isCollapsed ? "flex flex-col items-center space-y-2 " : "")}>
              <NavButton
                label="Support"
                icon={HelpCircle}
                isActive={false}
                onClick={() => {
                  window.open("https://support.canso.ai/support/tickets/new", "_blank");
                }}
                isCollapsed={isCollapsed}
              />
              <SidebarUserMenu isCollapsed={isCollapsed} />
            </div>
          </div>
        </div>
      </aside>

      <SidebarCollapseButton isCollapsed={isCollapsed} toggleCollapse={toggleCollapse} />
    </div>
  );
};

export default CaseNavigationSidebar;
